import { Icon } from '@dealstack/frontend/shared/components/fundament/Icon/Icon.tsx';
import { useTranslationCapManage } from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks.tsx';
import { Button, Drawer, Flex, MultiSelect } from '@mantine/core';
import React, { useEffect } from 'react';
import { FormProvider } from 'react-hook-form';
import { Select, TextInput } from 'react-hook-form-mantine';
import { ADDRESS_FORM_FIELDS, useAddressController } from './useAddressController.ts';
import { AddressRes, AddressTag } from '@dealstack/shared/lib/generated';
import { COUNTRIES_GROUPED_OPTIONS } from '@dealstack/shared/lib/countries/countriesData.ts';
import { drawerTransitionProps } from '@dealstack/frontend/shared/mechanism/styleguide/GlobalStyling.tsx';
import { TEST_ELEMENT_IDS } from '@dealstack/shared/tests/testElementIds';
import { makeOptionWithTestId } from '@dealstack/frontend/shared/components/Select/withOptionTestId';

const CountryOption = makeOptionWithTestId(TEST_ELEMENT_IDS.CAP_COUNTRY_OPTION);
const AddressTagOption = makeOptionWithTestId(TEST_ELEMENT_IDS.CAP_ADDRESS_FORM_TAG_OPTION);

interface Props {
	address: AddressRes | null;
	hasPermission: boolean;
	opened: boolean;
	onClose: () => void;
}

export const AddressDrawer = ({ address, hasPermission, opened, onClose }: Props) => {
	const tCapManage = useTranslationCapManage();
	const { addressForm, onFormSubmit, control, tagsOptions, requestLoading } =
		useAddressController();

	useEffect(() => {
		if (address) {
			addressForm.reset(address);
		} else {
			addressForm.reset();
		}
	}, [address]);

	const handleSubmit = () =>
		addressForm.handleSubmit((values) => onFormSubmit(values, address?.id))();

	return (
		<Drawer
			opened={opened}
			onClose={onClose}
			position="right"
			transitionProps={drawerTransitionProps}
			title={
				<span data-testid={TEST_ELEMENT_IDS.CAP_ADDRESS_DRAWER_HEADING}>
					{address
						? tCapManage('address.drawer.editTitle')
						: tCapManage('address.drawer.addTitle')}
				</span>
			}
		>
			<FormProvider {...addressForm}>
				<Flex direction="column" justify="space-between" h="100%">
					<Flex direction="column" gap="1rem">
						<TextInput
							name={ADDRESS_FORM_FIELDS.houseNumber}
							control={control}
							label={tCapManage('basic_details.address_form.house_number_label')}
							size="md"
							placeholder={tCapManage(
								'basic_details.address_form.house_number_placeholder'
							)}
							data-testid={TEST_ELEMENT_IDS.CAP_ADDRESS_FORM_HOUSE_NUMBER_INPUT}
						/>
						<TextInput
							name={ADDRESS_FORM_FIELDS.street}
							control={control}
							label={tCapManage('basic_details.address_form.street_label')}
							size="md"
							placeholder={tCapManage(
								'basic_details.address_form.street_placeholder'
							)}
							data-testid={TEST_ELEMENT_IDS.CAP_ADDRESS_FORM_STREET_INPUT}
						/>
						<TextInput
							name={ADDRESS_FORM_FIELDS.city}
							control={control}
							label={tCapManage('basic_details.address_form.town_label')}
							size="md"
							placeholder={tCapManage('basic_details.address_form.town_placeholder')}
							data-testid={TEST_ELEMENT_IDS.CAP_ADDRESS_FORM_CITY_INPUT}
						/>
						<Select
							name={ADDRESS_FORM_FIELDS.country}
							control={control}
							label={tCapManage('basic_details.address_form.country_label')}
							placeholder={tCapManage(
								'basic_details.address_form.country_placeholder'
							)}
							data={COUNTRIES_GROUPED_OPTIONS}
							searchable
							size="md"
							disabled={!hasPermission}
							data-testid={TEST_ELEMENT_IDS.CAP_ADDRESS_FORM_COUNTRY_SELECT}
							renderOption={CountryOption}
						/>
						<TextInput
							name={ADDRESS_FORM_FIELDS.postCode}
							control={control}
							label={tCapManage('basic_details.address_form.postcode_label')}
							size="md"
							placeholder={tCapManage(
								'basic_details.address_form.postcode_placeholder'
							)}
							data-testid={TEST_ELEMENT_IDS.CAP_ADDRESS_FORM_POST_CODE_INPUT}
						/>
						<MultiSelect
							data={tagsOptions}
							name={ADDRESS_FORM_FIELDS.tags}
							value={addressForm.watch(ADDRESS_FORM_FIELDS.tags)}
							onChange={(value: AddressTag[]) => {
								addressForm.setValue(ADDRESS_FORM_FIELDS.tags, value, {
									shouldDirty: true,
								});
							}}
							label={tCapManage('basic_details.address_form.tags_label')}
							placeholder={tCapManage('basic_details.address_form.tags_placeholder')}
							rightSection={<Icon variant="caret-down" size="lg" />}
							styles={{ rightSection: { pointerEvents: 'none' } }}
							inputWrapperOrder={['label', 'input', 'description']}
							size="md"
							clearable
							multiple
							data-testid={TEST_ELEMENT_IDS.CAP_ADDRESS_FORM_TAGS_MULTISELECT}
							renderOption={AddressTagOption}
						/>
					</Flex>

					<Button
						onClick={handleSubmit}
						disabled={requestLoading}
						data-testid={TEST_ELEMENT_IDS.CAP_ADDRESS_FORM_SUBMIT_BUTTON}
					>
						{address
							? tCapManage('address.drawer.editButton')
							: tCapManage('address.drawer.addButton')}
					</Button>
				</Flex>
			</FormProvider>
		</Drawer>
	);
};
